import { AGWASuiteApiClientBase, ApiResponse } from '../agwasuite-api';
import { ApiFactory } from '../agwasuite-api-factory';
import type {
  DataTableDataRequest,
  GetActiveSessionsResult,
  SessionHistoryResult,
  GetActivitySummaryResult,
} from '../agwa-api-model';

/**
 * API client for user activity tracking operations.
 * Provides endpoints for viewing live sessions, session history, and activity summaries.
 * Only accessible to AGWA staff with MemberAdmin rights.
 */
export class ApiActivity extends AGWASuiteApiClientBase {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  /**
   * Get currently active live sessions.
   * Returns real-time data from in-memory tracking.
   * Requires MemberAdmin rights.
   */
  async getLiveSessions(
    request: DataTableDataRequest
  ): Promise<ApiResponse<GetActiveSessionsResult>> {
    return this.postExecute<DataTableDataRequest, GetActiveSessionsResult>(
      '/api/activity/live',
      request
    );
  }

  /**
   * Search session history with filtering, sorting, and pagination.
   * Returns historical session data from the database.
   * Requires MemberAdmin rights.
   */
  async getSessionHistory(
    request: DataTableDataRequest
  ): Promise<ApiResponse<SessionHistoryResult>> {
    return this.postExecute<DataTableDataRequest, SessionHistoryResult>(
      '/api/activity/history',
      request
    );
  }

  /**
   * Get aggregated activity summaries with filtering, sorting, and pagination.
   * Returns hourly/daily aggregated usage data.
   * Requires MemberAdmin rights.
   */
  async getActivitySummary(
    request: DataTableDataRequest
  ): Promise<ApiResponse<GetActivitySummaryResult>> {
    return this.postExecute<DataTableDataRequest, GetActivitySummaryResult>(
      '/api/activity/summary',
      request
    );
  }
}

/**
 * Get an ApiActivity instance.
 * Creates a fresh instance each call to ensure current auth tokens.
 */
export function getActivityApi(): ApiActivity {
  return ApiFactory.authenticated(ApiActivity);
}
